import { guardArray } from "@msuite/utils/array";
import React from "react";
import {
	Area,
	Brush,
	AreaChart as RechartsAreaChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
} from "recharts";
import type { CurveType } from "recharts/types/shape/Curve";
import { ChartWrapper, type ChartWrapperProps } from "../chart-wrapper";
import { CartesianGrid } from "../components/cartesian-grid";
import { LinearGradient } from "../components/linear-gradient";
import { TooltipContent } from "../components/tooltip-content";
import type { ChartData, ChartDataProps, DataKey } from "../types";

/** Props Interface */
interface AreaChartProps<T extends ChartData>
	extends Omit<ChartWrapperProps, "children" | "isLoading">,
		ChartDataProps<T> {
	displayKeyMap: Record<DataKey<T>, string>;
	formatter?: (value: number | undefined) => React.ReactNode;
	xAxisVisible?: boolean;
	withBrush?: boolean;
	tickFormatter?: (value: string) => string;
	curveType?: CurveType;
}

export function AreaChart<T extends ChartData>({
	data,
	dataKeys,
	formatter,
	displayKeyMap,
	dataKeyClasses,
	xAxisVisible,
	withBrush,
	tickFormatter,
	curveType = "linear",
}: AreaChartProps<T>) {
	/** Render */
	return (
		<ChartWrapper isLoading={!data}>
			<div className="w-full h-full">
				<ResponsiveContainer
					width="100%"
					height="100%"
				>
					<RechartsAreaChart
						data={data}
						margin={{ top: 5, right: 0, left: 0, bottom: 0 }}
					>
						<defs>
							<LinearGradient id={"colorUv"} />
							{guardArray(Object.keys(dataKeyClasses || {})).map((key) => (
								<LinearGradient
									id={`linear-gradient-${key}`}
									key={key}
									startColor={`var(--color-${dataKeyClasses?.[key]}-500)`}
									endColor={`var(--color-${dataKeyClasses?.[key]}-500)`}
									withGradientOpacity
								/>
							))}
						</defs>
						<CartesianGrid />
						<XAxis
							hide={!xAxisVisible}
							dataKey="name"
							tickFormatter={tickFormatter}
							tickLine={false}
							fontSize={9}
						/>
						<Tooltip
							cursor={{
								className: "fill-primaryGray-500/10",
							}}
							content={(props) => {
								return (
									<TooltipContent
										{...props}
										displayKeyMap={displayKeyMap}
										formatter={formatter}
									/>
								);
							}}
							animationDuration={100}
							animationEasing="ease-out"
						/>
						{dataKeys.map((key, index) => (
							<Area
								key={key as string}
								type={curveType}
								dataKey={key as string}
								fill={
									dataKeyClasses?.[key]
										? `var(--color-${dataKeyClasses[key]}-500)`
										: "url(#colorUv)"
								}
								fillOpacity={dataKeyClasses?.[key] ? 0.2 : undefined}
								stroke={
									dataKeyClasses?.[key]
										? `var(--color-${dataKeyClasses[key]}-500)`
										: "var(--color-chart-base-1)"
								}
								style={{
									filter: !dataKeyClasses?.[key] ? `brightness(${index * 5}deg)` : undefined,
								}}
							/>
						))}
						{withBrush && <Brush height={10} />}
					</RechartsAreaChart>
				</ResponsiveContainer>
			</div>
		</ChartWrapper>
	);
}
